import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetProducts, useDeleteProduct } from '@/services/api/endpoints/ProductService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatAmount } from '@/lib/utils';
import type { Product } from '@/types';
import { ProductModal } from './ProductModal';

export function ProductsPage() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const { data, isLoading } = useGetProducts(page, 20, debouncedSearch, 'createdAt', 'desc');
  const deleteMutation = useDeleteProduct();

  const handleCreate = () => {
    setEditingProduct(null);
    setModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setModalOpen(true);
  };

  const handleDelete = (product: Product) => {
    if (!window.confirm(`${t('common.confirmDelete')} "${product.name}"?`)) return;
    deleteMutation.mutate(product.id, {
      onSuccess: () => toast.success(t('products.deleted')),
      onError: (error: any) => toast.error(error?.response?.data?.message || t('common.error')),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('products.title')}</h1>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          {t('products.create')}
        </Button>
      </div>

      <div className="max-w-sm">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="w-16 p-4 text-sm font-medium text-muted-foreground">{t('products.image')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('products.name')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.title')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('products.slotNumber')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('products.price')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((product) => (
                      <tr key={product.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="w-10 h-10 rounded-md object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                              <ImageOff className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-sm font-medium">{product.name}</td>
                        <td className="p-4 text-sm text-muted-foreground">{product.machine?.name || '-'}</td>
                        <td className="p-4 text-sm text-center">{product.slotNumber}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatAmount(product.price)}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={product.status} />
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(product)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((product) => (
                  <div key={product.id} className="p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="w-10 h-10 rounded-md object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                          <ImageOff className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{product.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{product.machine?.name || '-'}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('products.slotNumber')}: {product.slotNumber}</span>
                      <span className="font-medium">{formatAmount(product.price)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <StatusBadge status={product.status} />
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleEdit(product)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleDelete(product)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      <ProductModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        product={editingProduct}
      />
    </div>
  );
}
